import { TodoModel, RecurrenceFrequency } from '../../core/models/todo.model';
import { TodoService } from '../../core/services/todo.service';
import { BookingService } from '../../core/services/booking.service';
import { NotificationService } from '../../core/services/notification.service';
import { Component, EventEmitter, OnChanges, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ClinicStatusService } from '../../core/services/clinic-status.service';

@Component({
  selector: 'personal-schedule',
  imports: [CommonModule, FormsModule],
  templateUrl: './personal-schedule.html',
  standalone: true,
  styleUrls: ['./personal-schedule.scss'],
})
export class PersonalScheduleComponent implements OnInit, OnChanges {
  @Output() taskCreated = new EventEmitter<void>();

  newTodo: Partial<TodoModel> = {
    title: '',
    description: '',
    todoDate: '',
    priority: 'MEDIUM',
    completed: false,
    isRecurring: false,
    recurrencePattern: undefined,
    recurrenceEndDate: undefined,
    recurrenceIntervalDays: 1,
  };

  isRecurringTask = false;
  recurrenceFrequencies: RecurrenceFrequency[] = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'];
  frequencyLabels = {
    DAILY: 'Daily',
    WEEKLY: 'Weekly',
    MONTHLY: 'Monthly',
    YEARLY: 'Yearly',
  };

  useMockData = false;
  todos: TodoModel[] = [];
  isLoading = false;
  isClinicActive = false;
  isLinkedToClinic = false;

  showCreateForm = false;
  isSaving = false;

  today = new Date();

  constructor(
    private bookingService: BookingService,
    private todoService: TodoService,
    private notification: NotificationService,
    private clinicStatusService: ClinicStatusService,
  ) {}

  ngOnInit(): void {
    this.loadTodos();
    this.clinicStatusService.isClinicActive$.subscribe((active) => {
      this.isClinicActive = active;
    });
    this.clinicStatusService.hasClinic$.subscribe((has) => {
      this.isLinkedToClinic = has;
    });
  }

  ngOnChanges(): void {
    this.loadTodos();
  }

  loadMockTodos(): void {
    this.todos = [
      {
        id: '1',
        title: 'Review lab results',
        description: 'Check pending blood test results for Dr. Smith',
        todoDate: this.today.toISOString(),
        priority: 'HIGH',
        completed: false,
      },
      {
        id: '2',
        title: 'Call patient follow-ups',
        description: 'Confirm next week appointments',
        todoDate: this.today.toISOString(),
        priority: 'MEDIUM',
        completed: false,
      },
      {
        id: '3',
        title: 'Prepare consultation room',
        description: 'Room 3 needs equipment check',
        todoDate: this.today.toISOString(),
        priority: 'LOW',
        completed: true,
      },
      {
        id: '4',
        title: 'Update doctor schedule',
        description: 'Sync calendar changes with booking system',
        todoDate: this.today.toISOString(),
        priority: 'HIGH',
        completed: false,
      },
    ] as TodoModel[];
  }

  loadTodos(): void {
    this.isLoading = true;

    if (this.useMockData) {
      this.loadMockTodos();
      this.isLoading = false;
      return;
    }

    const date = this.today.toISOString().split('T')[0];

    this.todoService.getStaffTodos(date).subscribe({
      next: (res) => {
        this.todos = res;
        this.isLoading = false;
      },
      error: (err) => {
        this.notification.error(err.error?.message || 'Failed to load schedule');
        this.isLoading = false;
      },
    });
  }

  toggleComplete(todo: TodoModel) {
    todo.completed = !todo.completed;
  }

  openCreateForm(): void {
    this.showCreateForm = true;
    this.isRecurringTask = false;

    this.newTodo = {
      title: '',
      description: '',
      todoDate: this.today.toISOString().split('T')[0],
      priority: 'MEDIUM',
      completed: false,
      isRecurring: false,
      recurrencePattern: undefined,
      recurrenceEndDate: undefined,
      recurrenceIntervalDays: 1,
    };
  }

  cancelCreate(): void {
    this.showCreateForm = false;
  }

  saveTodo(): void {
    if (!this.newTodo.title || !this.newTodo.todoDate) {
      this.notification.error('Title and date are required');
      return;
    }

    const todo: TodoModel = {
      id: null as any,
      title: this.newTodo.title!,
      description: this.newTodo.description,
      todoDate: this.newTodo.todoDate!,
      priority: this.newTodo.priority as any,
      completed: false,
      isRecurring: this.isRecurringTask,
      recurrencePattern: this.isRecurringTask ? this.newTodo.recurrencePattern : undefined,
      recurrenceEndDate: this.isRecurringTask ? this.newTodo.recurrenceEndDate : undefined,
      recurrenceIntervalDays: this.isRecurringTask
        ? this.newTodo.recurrenceIntervalDays
        : undefined,
    };

    this.isSaving = true;

    const createMethod = this.isRecurringTask
      ? this.todoService.createRecurringTodo(todo)
      : this.todoService.createTodo(todo);

    createMethod.subscribe({
      next: (res) => {
        this.showCreateForm = false;
        this.isSaving = false;
        this.loadTodos();
        this.taskCreated.emit();
        this.notification.success?.(
          this.isRecurringTask
            ? `Recurring task created - will repeat ${this.frequencyLabels[this.newTodo.recurrencePattern || 'DAILY']}`
            : 'Task created',
        );
      },
      error: (err) => {
        this.isSaving = false;
        this.notification.error(err.error?.message || 'Failed to create task');
      },
    });
  }

  deleteTodo(id: string): void {
    this.todoService.deleteTodo(id).subscribe({
      next: () => {
        this.loadTodos();
        this.notification.success?.('Task deleted');
      },
      error: (err) => {
        this.notification.error(err.error?.message || 'Failed to delete task');
      },
    });
  }

  markComplete(id: string): void {
    this.todoService.completeTodo(id).subscribe({
      next: () => {
        const todo = this.todos.find((t) => t.id === id);
        if (todo) {
          todo.completed = true;
        }
        this.loadTodos();
        this.notification.success?.('Task marked as complete');
      },
      error: (err) => {
        this.notification.error(err.error?.message || 'Failed to mark task as complete');
      },
    });
  }

  toggleRecurringMode(): void {
    this.isRecurringTask = !this.isRecurringTask;
    if (this.isRecurringTask && !this.newTodo.recurrencePattern) {
      this.newTodo.recurrencePattern = 'DAILY';
    }
  }

  getMinEndDate(): string {
    const startDate = new Date(this.newTodo.todoDate || this.today);
    startDate.setDate(startDate.getDate() + 1);
    return startDate.toISOString().split('T')[0];
  }
}
